import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Calendar } from 'lucide-react';
import inventoryService, { MaterialConsumption } from '../../services/inventoryService';
import { formatDate } from '../../utils/dateUtils';
import DatePickerDropdown from '../../components/DatePickerDropdown';

const MaterialConsumptionView: React.FC = () => {
  const [productFilter, setProductFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch material consumption data
  const { data: consumptionData, isLoading, error } = useQuery(
    ['material-consumption', productFilter, startDate, endDate],
    () => inventoryService.getMaterialConsumption({
      product_sku: productFilter || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    })
  );

  // Get unique products
  const products = React.useMemo(() => {
    if (!consumptionData) return [];
    const uniqueProducts = new Set(
      consumptionData.map((c: MaterialConsumption) => `${c.product_sku}:${c.product_name}`)
    );
    return Array.from(uniqueProducts)
      .map(p => {
        const [sku, name] = p.split(':');
        return { sku, name };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [consumptionData]);

  // Group consumption by job/product
  const groupedConsumption = React.useMemo(() => {
    if (!consumptionData) return {};
    return consumptionData.reduce((acc: Record<string, MaterialConsumption[]>, item: MaterialConsumption) => {
      const key = `${item.job_number}-${item.product_sku}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [consumptionData]);

  // Calculate summary statistics
  const statistics = React.useMemo(() => {
    if (!consumptionData || consumptionData.length === 0) {
      return {
        totalJobs: 0,
        totalMaterials: 0,
        totalCost: 0,
        avgConsumptionRate: 0,
      };
    }

    const uniqueJobs = new Set(consumptionData.map((c: MaterialConsumption) => c.job_number));
    const uniqueMaterials = new Set(consumptionData.map((c: MaterialConsumption) => c.material_sku));
    const totalCost = consumptionData.reduce((sum: number, c: MaterialConsumption) => sum + (c.material_cost * c.input_quantity), 0);
    const avgRate = consumptionData.reduce((sum: number, c: MaterialConsumption) => sum + c.consumption_rate, 0) / consumptionData.length;

    return {
      totalJobs: uniqueJobs.size,
      totalMaterials: uniqueMaterials.size,
      totalCost,
      avgConsumptionRate: avgRate,
    };
  }, [consumptionData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Material Consumption</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track raw material usage and production efficiency
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="product" className="block text-sm font-medium text-gray-700 mb-1">
              Product
            </label>
            <select
              id="product"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Products</option>
              {products.map(product => (
                <option key={product.sku} value={product.sku}>
                  {product.name} ({product.sku})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <DatePickerDropdown
              selected={startDate || null}
              onSelect={(date) => setStartDate(date)}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-left">
                  <span>{startDate ? (() => { const d = new Date(startDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}</span>
                  <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
              )}
            </DatePickerDropdown>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <DatePickerDropdown
              selected={endDate || null}
              onSelect={(date) => setEndDate(date)}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-left">
                  <span>{endDate ? (() => { const d = new Date(endDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}</span>
                  <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
              )}
            </DatePickerDropdown>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setProductFilter('');
                setStartDate('');
                setEndDate('');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-sm text-gray-500">Total Jobs</div>
          <div className="text-2xl font-bold text-gray-900">{statistics.totalJobs}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-sm text-gray-500">Materials Used</div>
          <div className="text-2xl font-bold text-gray-900">{statistics.totalMaterials}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-sm text-gray-500">Total Material Cost</div>
          <div className="text-2xl font-bold text-gray-900">${statistics.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-sm text-gray-500">Avg Consumption Rate</div>
          <div className="text-2xl font-bold text-gray-900">{statistics.avgConsumptionRate.toFixed(2)}</div>
          <div className="text-xs text-gray-500">units per output</div>
        </div>
      </div>

      {/* Consumption Details */}
      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          Loading consumption data...
        </div>
      ) : error ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center text-red-500 dark:text-red-400">
          Failed to load consumption data
        </div>
      ) : Object.keys(groupedConsumption).length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No consumption data found for the selected filters
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedConsumption).map(([key, materials]) => {
            const firstItem = materials[0];
            const totalMaterialCost = materials.reduce(
              (sum: number, m: MaterialConsumption) => sum + (m.material_cost * m.input_quantity),
              0
            );

            return (
              <div key={key} className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                {/* Job Header */}
                <div className="bg-indigo-50 px-6 py-4 border-b border-indigo-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Job #{firstItem.job_number}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Product: <span className="font-medium">{firstItem.product_name}</span> ({firstItem.product_sku})
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Production Date</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatDate(firstItem.production_date)}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Output: <span className="font-medium">{firstItem.output_quantity.toLocaleString()}</span> units
                      </div>
                    </div>
                  </div>
                </div>

                {/* Materials Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Material SKU
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Material Name
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity Used
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unit
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unit Cost
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Cost
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Consumption Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {materials.map((material: MaterialConsumption, index: number) => (
                        <tr key={`${material.material_sku}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {material.material_sku}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {material.material_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {material.input_quantity.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {material.material_unit}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            ${material.material_cost.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            ${(material.material_cost * material.input_quantity).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {material.consumption_rate.toFixed(3)}
                          </td>
                        </tr>
                      ))}
                      {/* Summary Row */}
                      <tr className="bg-gray-50 dark:bg-gray-700 font-medium">
                        <td colSpan={5} className="px-6 py-3 text-right text-sm text-gray-700">
                          Total Material Cost for this Job:
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-bold">
                          ${totalMaterialCost.toFixed(2)}
                        </td>
                        <td className="px-6 py-3"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MaterialConsumptionView;
